import { useEffect, useRef, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ExtractionDialogFrame } from "@/components/save/ExtractionDialogFrame";
import { ExtractionDocumentPicker } from "@/components/save/ExtractionDocumentPicker";
import { FileDropZone } from "@/components/common/FileDropZone";
import { ANALYZABLE_ACCEPT, isAnalyzableDocument } from "@/lib/analyzable-documents";
import { MAX_DOCUMENTS_PER_RUN } from "@/lib/upload-limits";
import { ApplyOrganizationalPowerAnalysisDocument, OrganizationalPowerAnalysisDocument,
  StartOrganizationalPowerAnalysisDocument, type OrganizationalPowerAnalysisResultFragment } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { OrganizationalPowerTraitList } from "./OrganizationalPowerTraitList";

type AnalysisDocument = { id: string; title: string; fileName?: string; createdAt?: string | null };

export function OrganizationalPowerAnalysisDialog({ recordId, personId, documents, open = true, onClose, onUpload }: {
  recordId: string; personId: string; documents: readonly AnalysisDocument[]; open?: boolean; onClose: () => void;
  onUpload?: (files: File[]) => Promise<readonly AnalysisDocument[]>;
}) {
  const intl = useIntl();
  const client = useApolloClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [submitted, setResult] = useState<OrganizationalPowerAnalysisResultFragment | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadInFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [start, starting] = useMutation(StartOrganizationalPowerAnalysisDocument, { fetchPolicy: "no-cache" });
  const [apply, applying] = useMutation(ApplyOrganizationalPowerAnalysisDocument, { fetchPolicy: "no-cache" });
  const query = useQuery(OrganizationalPowerAnalysisDocument, { variables: { id: submitted?.id ?? "" },
    skip: submitted?.status !== "RUNNING", fetchPolicy: "no-cache", pollInterval: 2000 });
  const scopeMatches = (value: OrganizationalPowerAnalysisResultFragment) => value.recordId === recordId && value.subjectPersonId === personId;
  const current = query.data?.organizationalPowerAnalysis;
  const currentMatches = current?.id === submitted?.id && current?.recordId === recordId && current?.subjectPersonId === personId;
  const result = submitted?.status === "RUNNING" && currentMatches && current ? current : submitted;
  const readError = submitted?.status !== "RUNNING" ? null : query.error ? userErrorMessage(intl, query.error)
    : current && !currentMatches ? userErrorMessage(intl, new Error("Unexpected analysis scope")) : null;
  const running = result?.status === "RUNNING" && !readError;
  const stopPolling = query.stopPolling;
  useEffect(() => {
    if (readError || (current && (!currentMatches || current.status !== "RUNNING"))) stopPolling();
  }, [current, currentMatches, readError, stopPolling]);
  const busy = uploading || starting.loading || applying.loading;
  const selectedDocuments = documents.filter(doc => selected.includes(doc.id));
  const total = selectedDocuments.length;
  const failed = result?.status === "FAILED";
  const ready = result?.status === "READY";
  const applied = result?.status === "APPLIED";

  function reset() {
    stopPolling();
    setResult(null);
    setSelected([]);
    setError(null);
  }

  async function analyse() {
    if (busy || uploadInFlight.current || !total) return;
    setError(null);
    try {
      const response = await start({ variables: { recordId, documentIds: selectedDocuments.map(doc => doc.id) } });
      const value = response.data?.startOrganizationalPowerAnalysis;
      if (!value || !scopeMatches(value)) throw new Error("Unexpected analysis scope");
      setResult(value);
    } catch (failure) { setError(userErrorMessage(intl, failure)); }
  }

  async function accept() {
    if (!result || !scopeMatches(result) || !ready) return;
    setError(null);
    try {
      const response = await apply({ variables: { id: result.id } });
      const value = response.data?.applyOrganizationalPowerAnalysis;
      if (!value || !scopeMatches(value) || value.status !== "APPLIED") throw new Error("Analysis application was not confirmed");
      setResult(value);
      client.cache.evict({ id: client.cache.identify({ __typename: "Person", id: personId }), fieldName: "smartAnalysisResults" });
      void client.refetchQueries({ include: ["PersonProfile", "PowersData"] }).catch(() => undefined);
    } catch (failure) { setError(userErrorMessage(intl, failure)); }
  }

  async function upload(files: FileList) {
    if (!onUpload || uploadInFlight.current || busy || result) return;
    setError(null);
    const incoming = Array.from(files);
    if (incoming.length + total > MAX_DOCUMENTS_PER_RUN) {
      setError(intl.formatMessage({ id: "save.extraction.tooMany" }, { max: MAX_DOCUMENTS_PER_RUN }));
      return;
    }
    if (incoming.some(file => !isAnalyzableDocument(file.name, file.type))) {
      setError(intl.formatMessage({ id: "organizationalRecord.uploadRejected" }));
      return;
    }
    uploadInFlight.current = true;
    setUploading(true);
    try {
      const added = await onUpload(incoming);
      setSelected(previous => [...new Set([...previous, ...added.map(doc => doc.id)])]);
    } catch (failure) { setError(userErrorMessage(intl, failure)); }
    finally { uploadInFlight.current = false; setUploading(false); }
  }

  return <ExtractionDialogFrame id="organizational-power-dialog" open={open} onClose={onClose}
    preventClose={busy} closeId="organizational-power-close" title={intl.formatMessage({ id: "service.page.powerAnalysisTitle" })}
    footer={<div className="flex items-center justify-between gap-3 border-t border-ink/10 px-5 py-3">
      <span id="organizational-power-selection" className="text-smycles-label text-ink-muted">
        {!result && <FormattedMessage id={total ? "save.extraction.selectedCount" : "save.extraction.nothingSelected"} values={{ total }} />}
        {running && <FormattedMessage id="organizationalPower.background" />}
      </span>
      {(!result || running) && <Button id="organizational-power-start" disabled={busy || running || !total}
        aria-busy={busy || running} onClick={() => void analyse()}
        size="compact" className="min-w-[10rem] gap-2">
        {(busy || running) && <span aria-hidden className="h-3 w-3 shrink-0 animate-spin rounded-full border border-page/40 border-t-page" />}
        <FormattedMessage id={uploading ? "organizationalPower.uploading" : starting.loading || running ? "save.extraction.analysing" : "save.extraction.startAnalysis"} />
      </Button>}
      {ready && !!result.traits.length && <Button id="organizational-power-apply" disabled={busy || !!readError} onClick={() => void accept()}><FormattedMessage id="organizationalPower.apply" /></Button>}
      {(readError || failed || applied || (ready && !result.traits.length)) && <Button id="organizational-power-restart"
        disabled={busy} onClick={reset}><FormattedMessage id={readError || failed ? "common.tryAgain" : "organizationalPower.new"} /></Button>}
    </div>}>
      {(!result || running) && <>
        {!running && onUpload && <div>
          <div className="smycles-field-label mb-1.5 text-ink-muted"><FormattedMessage id="save.extraction.newFile" /></div>
          <FileDropZone inputId="organizational-power-file" variant="zone" clickToOpen accept={ANALYZABLE_ACCEPT}
            disabled={busy} onFiles={files => void upload(files)}
            label={<FormattedMessage id="save.extraction.dropZone" />} hint={<FormattedMessage id="save.extraction.dropZoneHint" />} />
          <p className="mt-3 text-smycles-label text-ink-muted"><FormattedMessage id="organizationalPower.uploadDestination" /></p>
        </div>}
        <div className="smycles-hint">
          <div className="text-smycles-micro tracking-[0.2em] text-ink-accent"><FormattedMessage id="save.extraction.guideTitle" /></div>
          <ul className="mt-1 smycles-field-meta list-disc space-y-1 pl-4 leading-relaxed text-ink marker:text-ink-muted">
            <li><FormattedMessage id="organizationalPower.guidePerson" /></li>
            <li><FormattedMessage id="organizationalPower.guideConcrete" /></li>
          </ul>
        </div>
        {running ? <>
          <p role="status" className="smycles-field-meta text-ink"><FormattedMessage id="organizationalPower.running" /></p>
          <ul className="space-y-1">{selectedDocuments.map(doc => <li key={doc.id} className="box-flat truncate px-3 py-2 text-smycles-meta">{doc.title}</li>)}</ul>
        </> : <ExtractionDocumentPicker documentScope="organizational" docs={documents.filter(doc => isAnalyzableDocument(doc.fileName ?? doc.title)).map(doc => ({ id: doc.id, name: doc.title, fileName: doc.fileName, createdAt: doc.createdAt }))}
          selectedIds={new Set(selected)} room={Math.max(0, MAX_DOCUMENTS_PER_RUN - total)} disabled={busy}
          idPrefix="organizational-power" selectionIdPrefix="organizational-power-document"
          label={<FormattedMessage id="organizationalPower.fromRecord" />}
          onToggle={id => setSelected(previous => previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id])}
          onToggleMany={(ids, select) => setSelected(previous => select ? [...new Set([...previous, ...ids])].slice(0, MAX_DOCUMENTS_PER_RUN) : previous.filter(id => !ids.includes(id)))} />}
      </>}
      {failed && <p role="alert"><FormattedMessage id="organizationalPower.failed" /></p>}
      {ready && !readError && <>
        <p className="smycles-field-value text-ink-muted"><FormattedMessage id={result.traits.length ? "organizationalPower.review" : "organizationalPower.empty"} /></p>
        <OrganizationalPowerTraitList traits={result.traits} className="max-h-[50vh] overflow-auto" />
      </>}
      {applied && <p id="organizational-power-applied" role="status"><FormattedMessage id="organizationalPower.applied" /></p>}
      {(error || readError) && <p role="alert" className="smycles-field-meta text-danger">{readError ?? error}</p>}
  </ExtractionDialogFrame>;
}
